/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2016, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 * @module dashboard/filters/pagecontext/PageContextEntry
 * @see PageContextEntry
 */

define([
	'../../lib/@waca/dashboard-common/dist/core/Model',
	'./PageContextTupleSet',
	'./PageContextRangeConditions',
	'underscore',
	'../../lib/@waca/core-client/js/core-client/utils/UniqueId'
], function(Model, PageContextTupleSet, PageContextRangeConditions, _, UniqueID) {

	/**
	 * Model which represents a single entry in the pageContext collection (and can be persisted to the dashboard spec).
	 * This entry is externally exposed as
	 * 1) a pageContextItem (an object API) or
	 * 2) a pageContextSpecItem (a JSON spec for use in the idvisualizations's setContext api).
	 */
	var PageContextEntry = Model.extend({
		whitelistAttrs: ['origin',
			'table',
			'alias',
			'filterName',
			'sourceId',
			'synchDataId',
			'hierarchies',
			'tupleSet',
			'conditions',
			'scope',
			'metadataSourceId',
			'hierarchyNames',
			'hierarchyUniqueNames',
			'exclude',
			'eventSourceId',
			'eventGroupId',
			'isNamedSet',
			'isSynchronizedEntry',
			'isModeledFilter',
			'expression'
		],
		runtimeAttrs: ['tupleSet', 'exclude'],

		init: function(attrs, options) {
			PageContextEntry.inherited('init', this, arguments);

			//TODO: In future, it may be possible for a single entry to have BOTH rangeConditions and values.
			//		(ie: a range within a selected set).  For now, only one can apply.
			this.api = {
				getMetadataSourceId: this.getMetadataSourceId.bind(this),
				getItemName: this.getItemName.bind(this),
				getItemId: this.getItemId.bind(this),
				getItemIds: this.getItemIds.bind(this),
				getSourceId: this.getSourceId.bind(this),
				getScope: this.getScope.bind(this),
				getKey: this.getKey.bind(this),
				shouldOpenViewOnLoad:this.shouldOpenViewOnLoad.bind(this),
				isHierarchy: this.isAHierarchy.bind(this),
				getHierarchyCount: this._getHierarchyCount.bind(this),
				getIsNamedSet: this.getIsNamedSet.bind(this),
				isValueDataItem: this.isValueDataItem.bind(this),
				isRange: this.isRange.bind(this),
				getOrigin: this.getOrigin.bind(this),
				setOpenViewOnLoad:this.setOpenViewOnLoad.bind(this),
				getError: this.getError.bind(this),
				setError: this.setError.bind(this),
				getExclude: this.getExclude.bind(this),
				setExclude: this.setExclude.bind(this),
				getValueCount: this.getValueCount.bind(this),
				getGlobalContext: this.getGlobalContext.bind(this),
				getDataType: this.getDataType.bind(this),
				getUsage: this.getUsage.bind(this),
				updateEventGroupId: this.updateEventGroupId.bind(this),
				getEventGroupId: this.getEventGroupId.bind(this),
				getEventSourceId: this.getEventSourceId.bind(this),
				updateMetadata: this.updateMetadata.bind(this),
				resetToPersistedValues: this.resetToPersistedValues.bind(this),
				getPageContextSpec: this.getPageContextSpecItem.bind(this),
				getPageContextSpecItem: this.getPageContextSpecItem.bind(this),
				getMetadataInfo: this.getMetadataInfo.bind(this),
				getSynchDataId: this.getSynchDataId.bind(this),
				setSynchDataId: this.setSynchDataId.bind(this),
				resetSynchDataId: this.resetSynchDataId.bind(this),
				hasData: this.hasData.bind(this),
				getBinningAPI: this.getBinningAPI.bind(this),
				getBinningAPIs: this.getBinningAPIs.bind(this),
				getIsSynchronizedEntry: this.getIsSynchronizedEntry.bind(this),
				getIsColumnUnavailable: this.getIsColumnUnavailable.bind(this),
				getIsModeledFilter: this.getIsModeledFilter.bind(this)
			};

			//These objects will be initialized depending on the type of entry (range or member).
			this._rangeConditions = null;
			this._tupleSet = null;

			if (this.condition || this.conditions) {
				delete this.condition;
				this._rangeConditions = new PageContextRangeConditions({
					set: this.setRangeConditionsCb.bind(this),
					get: this.getRangeConditionsCb.bind(this)
				});
				this._extendAPI(this._rangeConditions.getAPI());
			}

			//Expect the tupleSet either as an object or a string.
			// if it's already an object we leave it alone.
			if (this.tupleSet && !_.isObject(this.tupleSet)) {
				//The tupleSet is serialized as a JSON string to avoid nested models in the collection.
				this.set({
					tupleSet: JSON.parse(this.tupleSet)
				}, options);
			}

			if (!this._rangeConditions) {
				this._tupleSet = new PageContextTupleSet({
					set: this.setTupleSetCb.bind(this),
					get: this.getTupleSetCb.bind(this),
					exclude: this.getExclude(),
					isNamedSet: this.getIsNamedSet(),
					hierarchies: this.hierarchies
				});
				this._extendAPI(this._tupleSet.getAPI());
			}

			//Set a content-based key for use in get() rather than an ambiguous modelId.
			this.id = this.getKey();

			//A callback function which can be set to allow access to the related global context for a tab filter.
			this._globalContextCb = null;
		},

		getBinningAPIs: function () {
			return this.binningAPIs && this.binningAPIs.length ? this.binningAPIs : null;
		},

		/**
		 * @returns the 'public api' for a pageContextEntry (a pageContextItem as returned by pageContext's getPageContextItems)
		 */
		getAPI: function() {
			return this.api;
		},

		/**
		 * @returns the origin of this entry (for now, one of 'visualization' or 'filter')
		 */
		getOrigin: function() {
			return this.origin;
		},

		/**
		 * Potential values include INTEGER, DATE, FLOAT
		 */
		getDataType: function() {
			return this.dataType;
		},

		/**
		 * @returns the usage of this entry. Usage value is either 'fact' or 'attribute'
		 */
		getUsage: function() {
			return this.usage;
		},

		/**
		 * Indicates the view of this page context entry should be opened
		 * when loaded in a container such as the filter dock
		 */
		setOpenViewOnLoad: function(value) {
			this.openViewOnLoad = value;
		},

		/**
		 * @returns true if this pageContextEntry is of type range.
		 */
		isRange: function() {
			return (this._rangeConditions !== null);
		},

		getError: function() {
			return this.error;
		},

		setError: function(error) {
			this.error = error;
		},

		/**
		 * add API methods from either a range or member filter to the basic api for a context entry
		 * @param additionAPIMethods - the additional methods (eg getFrom for a range filter)
		 */
		_extendAPI: function(additionalAPIMethods) {
			_.each(additionalAPIMethods, function(apiMethod, key) {
				this.api[key] = apiMethod;
			}.bind(this));
		},

		/**
		 * If this is a tab filter, we need to be able to acces the related globalContext for this item if there is one.
		 */
		getGlobalContext: function() {
			return (this._globalContextCb) ? this._globalContextCb() : null;
		},

		/**
		 * Called by the parent pageContext on item initialization if this item is a tab filter and has a global counterpart.
		 * @param globalContextCb - a callback function which can be used to obtain the global counterpart of this tab filter.
		 */
		setGlobalContextCb: function(globalContextCb) {
			this._globalContextCb = globalContextCb;
			if (this._tupleSet) {
				this._tupleSet.setGlobalContextCb(globalContextCb);
			}
			if (this._rangeConditions) {
				this._rangeConditions.setGlobalContextCb(globalContextCb);
			}
		},

		/**
		 * getPageContextItems API Method:
		 * @param name - set filter name
		 */
		setFilterName: function(name) {
			this.filterName = name;
		},

		/**
		 * getPageContextItems API Method:
		 * @returns the user-friendly name of the item (could also be a user-set filter name)
		 */
		getItemName: function() {
			var hierarchyNames = this.hierarchyNames || [];
			if (this.binningAPIs && this.binningAPIs.length > 0) {
				var binningInfo = this.getBinningAPI();
				if (binningInfo) {
					var binningLabel = binningInfo.binningAPI.label;
				}
			}
			return binningLabel || this.filterName || hierarchyNames[0] || '';
		},

		/**
		 * getPageContextItems API Method:
		 * @returns the id of the item (aka the hierarchyUniqueName)
		 */
		getItemId: function(tupleIdx) {
			tupleIdx = tupleIdx || 0;
			return this.hierarchyUniqueNames[tupleIdx];
		},

		/**
		 * @returns the id of all items of a page context item entry (nested projections may have many item ids)
		 */
		getItemIds: function() {
			return this.hierarchyUniqueNames;
		},

		getBinningAPI: function(tupleIdx) {
			const binningAPI = this.getBinningAPIs();
			if (binningAPI) {
				var itemId = this.getItemId(tupleIdx);
				return this.binningAPIs.find(function (binningInfo) {
					return binningInfo.itemId === itemId;
				});
			}
			return null;
		},

		/**
		 * getPageContextAPI Method:
		 * @returns the sourceId of the table this item is from
		 */
		getSourceId: function() {
			return this.sourceId;
		},

		isAHierarchy: function() {
			return this.isHierarchy;
		},

		getIsColumnUnavailable: function() {
			return this.isColumnUnavailable;
		},

		/**
		 * provide 'get' model access to the isNamedSet property.
		 * @returns the saved isNamedSet flag
		 */
		getIsNamedSet: function() {
			return !!this.isNamedSet;
		},

		// Item is a full set or a data item expression, and there is no value for the item.
		isValueDataItem: function() {
			return this.getIsNamedSet() && this.getValueCount() === 0;
		},

		/**
		 * @return true if the page context entry is created from another data source.
		 */
		getIsSynchronizedEntry: function() {
			return this.isSynchronizedEntry;
		},

		shouldOpenViewOnLoad: function() {
			return this.openViewOnLoad && !this.isModeledFilter;
		},

		/**
		 * getPageContextAPI Method:
		 * @returns the sourceId of the table this item is from
		 */
		getScope: function() {
			return this.scope;
		},

		/**
		 * @returns the id of the metadataSource that this object is based on....can change with binding.
		 */
		getMetadataSourceId: function() {
			return this.metadataSourceId;
		},

		getIsModeledFilter: function() {
			return !!this.isModeledFilter;
		},

		/**
		 * @returns the 'unscoped key' for this pageContextEntry which is the uniqueKey minus the scope.
		 *			This is useful for providing a net context (global and tab-specific keys for the same thing are identical).
		 */
		getUnscopedKey: function() {
			var keyProperties = this._getBaseKeyProps();
			keyProperties.huns = this._getHierarchies();
			delete keyProperties.scope;

			return JSON.stringify(keyProperties);
		},

		/**
		 * @returns the full key for this pageContextEntry which is the uniqueKey including the scope.
		 */
		getKey: function() {
			var keyProperties = this._getBaseKeyProps();
			keyProperties.huns = this._getHierarchies();
			return JSON.stringify(keyProperties);
		},

		/**
		 * @returns the 'global key' for this pageContextEntry which is the uniqueKey with the scope replaced with 'global'.
		 *			This is useful for accessing the global counterpart to a tab-based filter.
		 */
		getGlobalKey: function() {
			var keyProperties = this._getBaseKeyProps();
			keyProperties.scope = 'global';
			keyProperties.huns = (this.hierarchyUniqueNames);
			return JSON.stringify(keyProperties);
		},

		_getBaseKeyProps: function() {
			var props = {
				origin: this.origin,
				sourceId: this.sourceId,
				scope: this.scope
			};

			if (this.eventGroupId) {
				props.eventGroupId = this.eventGroupId;
			}

			return props;
		},

		_getHierarchyCount: function() {
			return this.hierarchies.length;
		},

		/**
		 * for visualization entries (brushing), the hierarchies structure is persisted (a single brushing entry relates to 1 or many hiearchies depending on the number of edges that intersect to compose a datapoint on a visualization.)
		 * for filter entries, hierarchyUniqueNames are persisted.  a single filter entry ....this corresponds to an older version of the pageContext Specification
		 *	(hierarchyUniqueNames are converted to hierarchies form when producing a netPageContext for the vizControl)
		 * TODO: Filters should change to persist hierarchies in future as a 'silent upgrade'.
		 * @returns an array containing information describing the set of hierarchies the item
		 */
		_getHierarchies: function() {

			var tempInfo;
			if (this.origin === 'visualization' && this.hierarchies) {
				tempInfo = _.clone(this.hierarchies); //Make a copy to sort and keep the source unchanged.
				return tempInfo.sort(function(a, b) {
					return (a.hierarchyUniqueName > b.hierarchyUniqueName) ? 1 : 0;
				});
			} else if (this.hierarchyUniqueNames) {
				tempInfo = _.clone(this.hierarchyUniqueNames); //Make a copy to sort and keep the source unchanged.
				return tempInfo.sort();
			}
			return null;
		},

		/**
		 * provide 'set' model access to the rangeConditions object.
		 * @param rangeConditions - a conditions descriptor (array of from/to ranges etc.)
		 * @param options - options for model update
		 */
		setRangeConditionsCb: function(rangeConditions, options) {
			this.set({
				conditions: rangeConditions
			}, options);
		},

		/**
		 * provide 'get' model access to the rangeConditions object.
		 * @returns the saved range conditions.
		 */
		getRangeConditionsCb: function() {
			return this.conditions;
		},

		/**
		 * provide 'set' model access to the PageContextTupleSet object.
		 * @param tupleSet - the tupleSet to save.
		 * @param options - options for this set (such as transactionId)
		 */
		setTupleSetCb: function(tupleSet, options) {
			this.set({
				tupleSet: tupleSet
			}, options);
		},

		/**
		 * provide 'get' model access to the PageContextTupleSet object.
		 * @returns the saved tupleSet.
		 */
		getTupleSetCb: function() {
			return this.tupleSet;
		},

		/**
		 * provide 'set' model access to the exclude property.
		 * @param {boolean} state - the exclude state to save.
		 * @param {object} options - options to use in setting
		 */
		setExclude: function(state, options) {
			if (this.get('exclude') !== state) {
				this.set({
					exclude: state
				}, options);
				if (this._tupleSet) {
					this._tupleSet.setExclude(state);
				}
			}
		},

		/**
		 * provide 'get' model access to the exclude property.
		 * @returns the saved exclude flag
		 */
		getExclude: function() {
			return !!this.exclude;
		},

		/**
		 * provide 'set' model access to the exclude property.
		 * @param {string} id - the synchronized data identifier
		 * @param {object} options - options to use in setting
		 */
		setSynchDataId: function(id, options) {
			this.set({ synchDataId: id }, options);
		},

		getSynchDataId: function() {
			return this.synchDataId;
		},

		resetSynchDataId: function(options) {
			this.set({ synchDataId: null }, options);
		},

		/**
		 * The exclude flag of the spec to VisControl needs to cross check the exclude flag in global context as well as if it has selected members.
		 * This function is used in conjunction with PageContextTupleSet._intersectTuplesForSpec.
		 *
		 * @returns {boolean} there are 4 cases. see comments below
		 */
		_getExcludeForSpec: function() {
			if (this._tupleSet) {
				return this._tupleSet.getExcludeForSpec();
			}
			return this.getExclude();
		},

		/**
		 * @returns {number} the count of values
		 */
		getValueCount: function() {
			if (this._tupleSet) {
				return this._tupleSet.getValueCount();
			}
			return 0;
		},
		/**
		 * get the pageContextItem which is part of the pageContext specification provided to the viz control's setPageContext api.
		 * This is very similar to the internal form except it is 'cleaned up' to meet PageContext specification requirements.
		 * PageContextEntries are of type 'filter' (selection in a filter widget/dock) or 'visualization' (selection in a visualization).
		 * They also have a scope to define where they are applied (or how they are blended to produce a 'net filter state').
		 *
		 * Simplified Sample: PageContextSpecItem form
		 *
		 * //Discrete filter type....
		 * {
		 *	 "origin" : "filter",
		 *	 "sourceId" : "1",
		 *	 "table" : "0",
		 *	 "alias" : "0",
		 *	 "hierarchies: { hierarchyUniqueName : "04c343cd-6d89-4b42-a4e5-429eb33d67fa" },
		 *	 "tupleSet" : [
		 *		 ["[cube].[04c343cd-6d89-4b42-a4e5-429eb33d67fa].[04c343cd-6d89-4b42-a4e5-429eb33d67fa].[Year (Date)]->[all].[2014]"],
		 *		 ["[cube].[04c343cd-6d89-4b42-a4e5-429eb33d67fa].[04c343cd-6d89-4b42-a4e5-429eb33d67fa].[Month (Date)]->[all].[2013].[July]"],
		 *		 ["[cube].[04c343cd-6d89-4b42-a4e5-429eb33d67fa].[04c343cd-6d89-4b42-a4e5-429eb33d67fa].[Month (Date)]->[all].[2013].[August]"]
		 *	 ]
		 * },
		 *
		 * //Range filter type....
		 * {
		 *	 "origin" : "filter",
		 *	 "sourceId" : "1",
		 *	 "table" : "0",
		 *	 "alias" : "0",
		 *	 "hierarchyUniqueNames" : ["[0].[Year]"],
		 *	 "conditions" : [{
		 *			 "attributeUniqueNames" : ["[0].[Year]"], "from" : ["2004"], "to" : ["2009"] }
		 *	 ]
		 * }
		 * @param excludeSet - the JSON object will be returned without the specified members.
		 * @returns an object in specForm
		 */

		getPageContextSpecItem: function() {
			var pageContextSpecItem = {
				origin: this.origin,
				table: this.table || '',
				alias: this.alias,
				filterName: this.getItemName(),
				sourceId: this.sourceId,
				exclude: this._getExcludeForSpec(),
				hierarchies: (this.hierarchies) ? this.hierarchies : this._restructureHierarchiesForSpec(this.hierarchyUniqueNames, this.hierarchyNames),
				isModeledFilter: this.getIsModeledFilter(),
				error: null
			};

			if (this.getIsSynchronizedEntry()) {
				pageContextSpecItem.isSynchronizedEntry = true;
			}

			const binningAPIs = this.getBinningAPIs();
			if (binningAPIs) {
				pageContextSpecItem.binningAPIs = binningAPIs;
			}

			//For member type filtes, a tupleSet will be passed.  Otherwise conditions will be passed.
			if (this._tupleSet) {
				pageContextSpecItem.tupleSet = this._tupleSet.getSpec();
			}
			if (this._rangeConditions && this._rangeConditions.hasRangeCondition()) {
				//In spec form, if there are no range conditions, (or they define an empty range, they should not be returned as part of the spec.)
				pageContextSpecItem.conditions = this._rangeConditions.getSpec();
			}
			return pageContextSpecItem;
		},

		/**
		 * modify the hierarchyUniqueNames and hierarchyNames to be under a 'hierarchies' parent.
		 * This is needed for the pageContext spec.
		 * @param hierarchyUniqueNames - a list of hierarchyUniqueNames
		 * @returns the hierarchies in a form that is compatible with the pageContext spec.
		 */
		_restructureHierarchiesForSpec: function(hierarchyUniqueNames) {
			var hierarchies = [];
			for (var i = 0; i < hierarchyUniqueNames.length; ++i) {
				var hierarchy = {
					hierarchyUniqueName: hierarchyUniqueNames[i],
					attributes: []
				};
				hierarchies.push(hierarchy);
			}
			return hierarchies;
		},

		/**
		 * used by updatePageContext to update the values or range conditions when (for example) a user has selected or deselected values or adjusted range endpoints.
		 * @param update - an update specification which includes a command (update|remove) and an array of values
		 * @returns true if the values were modified.
		 */
		mergeValues: function(update, options) {
			if (!_.isUndefined(update.condition)) {
				if (!this._rangeConditions) {
					// note: this looks like it would cause problems since updateRangeConditions requires get and set functions,
					// see where PageContextRangeConditions is used in the init function... perhaps this line is never exercised?
					this._rangeConditions = new PageContextRangeConditions(this);
				}
				return this._rangeConditions.updateRangeConditions(update.condition, this.getItemId(), options);
			}

			return this._tupleSet.mergeValues(update, options);
		},

		/**
		 * add a new tupleSet to the existing one
		 * @param tupleSet - the tupleSet to append (which could contain one or more tuples to be added)
		 *                   (a tuple can be an edge entry like '2005' or can represent a datapoint (eg a bubble in a bubblechart) like '2005/Camping/Fax')
		 *					 each tuplePart is made up of a mun and a key).
		 * @param options - options to pass along to set (such as undo/Redo information).
		 */
		addToTupleSet: function(tupleSet, options) {
			if (this._tupleSet) {
				this._tupleSet.add(tupleSet, options);
			}
		},

		/**
		 * clear all values or range conditions (if any) for the current pageContextItem.
		 * @param options - if this is part of a bulk clear, the undoRedoTransactionId should be sent in the payloadData property.
		 * @returns true if the entry was modified
		 */
		clearValues: function(options) {
			var valuesCleared;
			if (this.conditions) {
				this.set({
					conditions: {}
				}, options);
				valuesCleared = true;
			} else if (this.tupleSet) {
				// clearing the values also means clearing the exclude
				if (this.get('exclude')) {
					if (!options) {
						options = {
							payloadData: {
								undoRedoTransactionId: UniqueID.get('clear_values_')
							}
						};
					}
					this.setExclude(false, options);
				}

				this.set({
					tupleSet: null
				}, options);
				valuesCleared = true;
			} else {
				this.setExclude(false, options);
				valuesCleared = true;
			}
			return valuesCleared;
		},

		/**
		 * update eventGroupId property with given new value
		 * @param newEventGroupId - new group id
		 * @param options
		 */
		updateEventGroupId: function(newEventGroupId, options) {
			this.set({
				eventGroupId: newEventGroupId
			}, options);
			// since eventGroupId used in contextEntry id, the id must get updated too
			this.set({
				id: this.getKey()
			}, options);

		},

		getEventGroupId: function() {
			return this.eventGroupId;
		},

		getEventSourceId: function() {
			return this.eventSourceId;
		},

		/**
		 * update properties that are from column metadata. This update is a silent update.
		 * @param metadataInfo - metadata info
		 * @param metadataInfo.dataType - dataType
		 * @param metadataInfo.usage - usage
		 * @param metadataInfo.isHierarchy - isHierarchy
		 */
		updateMetadata: function(metadataInfo) {
			this._metadataInfo = metadataInfo;
			if (metadataInfo.dataType) {
				this.dataType = metadataInfo.dataType;
			}
			if (metadataInfo.usage) {
				this.usage = metadataInfo.usage;
			}
			if (metadataInfo.isHierarchy !== undefined) {
				this.isHierarchy = metadataInfo.isHierarchy;
			}
			if (metadataInfo.isColumnUnavailable === true) {
				this.isColumnUnavailable = true;
			} else {
				this.isColumnUnavailable = false;
			}
		},

		updateConditions: function() {
			if (this.usage === 'attribute') {
				delete this.conditions;
				this._rangeConditions = null;
				this._tupleSet = new PageContextTupleSet({
					set: this.setTupleSetCb.bind(this),
					get: this.getTupleSetCb.bind(this),
					exclude: this.getExclude(),
					isNamedSet: this.getIsNamedSet(),
					hierarchies: this.hierarchies
				});
				this._extendAPI(this._tupleSet.getAPI());
			} else {
				this.conditions = [];
				this._tupleSet = null;
				delete this.condition;
				this._rangeConditions = new PageContextRangeConditions({
					set: this.setRangeConditionsCb.bind(this),
					get: this.getRangeConditionsCb.bind(this)
				});
				this._extendAPI(this._rangeConditions.getAPI());
			}
		},
		/**
		 * Convert this to a form compatible for saving to the dashboard spec.
		 * Currently, this is equivalent to the internal form of whitelist attributes except that the
		 * tupleset is a complex object and is stringified prior to save.
		 */
		toJSON: function() {
			// to JSON must use the values returned from 'getPersisted' and not get or the member variables directly.
			// the values returned by get could by the runtimeOnly ones and should not be persisted permanently/
			// Model.js takes care of managing the runtime state state and may return different values from get() and getPersisted()

			var json = PageContextEntry.inherited('toJSON', this, [
				[],
				['tupleSet']
			]);
			var tupleSet = this.getPersisted('tupleSet');
			if (tupleSet !== undefined) {
				json.tupleSet = JSON.stringify(tupleSet);
			}
			return json;
		},

		/**
		 * reset the values to the persisted ones. I.e not runtime
		 *
		 * @param {Object} options - model options, like transaction id.
		 * @returns {Boolean} true if any changes where done.
		 */
		resetToPersistedValues: function(options) {
			var modified = false;
			// should match runtimeAttrs array. I.e tupleSet and exclude
			if (this.get('exclude') !== this.getPersisted('exclude')) {
				modified = true;
				this.setExclude(this.getPersisted('exclude'), options);
			}
			if (this.get('tupleSet') !== this.getPersisted('tupleSet')) {
				modified = true;
				this.setTupleSetCb(this.getPersisted('tupleSet'), options);
			}
			return modified;
		},

		getMetadataInfo: function() {
			return this._metadataInfo;
		},

		hasData: function() {
			if (this._tupleSet) {
				return this._tupleSet.hasData();
			} else if (this._rangeConditions) {
				return this._rangeConditions.hasData();
			}
			return false;

		}
	});

	return PageContextEntry;
});
